import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import NewButton from '../../components/NewButton/NewButton';
import { dateOnlyBodyTemplate, formatDateForApi } from '../../utils/dateUtils';

// Mock data for demonstration
const mockEstimates = [
    {
        id: 1,
        orderNumber: 'ORD-7845',
        customerName: 'Mega Motor A.Ş.',
        productCode: 'PT-12345',
        productName: 'Şanzıman Dişlisi',
        orderDate: '2025-03-15',
        originalEstimatedDate: '2025-04-10',
        currentEstimatedDate: '2025-04-10',
        estimatedShipmentDate: '2025-04-09',
        reasonForChange: '',
        status: 'OnTime',
        priority: 'Normal',
        responsiblePerson: 'Ahmet Yılmaz'
    },
    {
        id: 2,
        orderNumber: 'ORD-7846',
        customerName: 'Anadolu Otomotiv Ltd.',
        productCode: 'PT-45678',
        productName: 'Fren Balataları',
        orderDate: '2025-03-17',
        originalEstimatedDate: '2025-04-12',
        currentEstimatedDate: '2025-04-18',
        estimatedShipmentDate: '2025-04-16',
        reasonForChange: 'Tedarikçi gecikmesi',
        status: 'Delayed',
        priority: 'High',
        responsiblePerson: 'Mehmet Kaya'
    },
    {
        id: 3,
        orderNumber: 'ORD-7847',
        customerName: 'Yıldız Makine San.',
        productCode: 'PT-98765',
        productName: 'Motor Valfleri',
        orderDate: '2025-03-18',
        originalEstimatedDate: '2025-04-20',
        currentEstimatedDate: '2025-04-15',
        estimatedShipmentDate: '2025-04-14',
        reasonForChange: 'Öncelikli müşteri talebi',
        status: 'Expedited',
        priority: 'Urgent',
        responsiblePerson: 'Ayşe Demir'
    }
];

const priorityOptions = [
    { name: 'Acil', code: 'Urgent' },
    { name: 'Yüksek', code: 'High' },
    { name: 'Normal', code: 'Normal' },
    { name: 'Düşük', code: 'Low' }
];

const statusOptions = [
    { name: 'Zamanında', code: 'OnTime' },
    { name: 'Gecikmiş', code: 'Delayed' },
    { name: 'Riskli', code: 'AtRisk' },
    { name: 'Hızlandırılmış', code: 'Expedited' }
];

const EstimatedDeliveryDatePage = () => {
    const [estimates, setEstimates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        orderNumber: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
        },
        customerName: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        },
        productCode: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
        },
        productName: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        },
        status: { value: null, matchMode: FilterMatchMode.EQUALS },
        priority: { value: null, matchMode: FilterMatchMode.EQUALS },
        responsiblePerson: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        }
    });
    const [displayModal, setDisplayModal] = useState(false);
    const [selectedEstimate, setSelectedEstimate] = useState(null);
    const [formData, setFormData] = useState({
        orderNumber: '',
        customerName: '',
        productCode: '',
        productName: '',
        orderDate: new Date(),
        currentEstimatedDate: new Date(),
        estimatedShipmentDate: new Date(),
        status: 'OnTime',
        priority: 'Normal',
        responsiblePerson: '',
        reasonForChange: ''
    });

    useEffect(() => {
        setEstimates(mockEstimates);
        setLoading(false);
    }, []);

    useEffect(() => {
        if (selectedEstimate) {
            setFormData({
                orderNumber: selectedEstimate.orderNumber,
                customerName: selectedEstimate.customerName,
                productCode: selectedEstimate.productCode,
                productName: selectedEstimate.productName,
                orderDate: new Date(selectedEstimate.orderDate),
                currentEstimatedDate: new Date(selectedEstimate.currentEstimatedDate),
                estimatedShipmentDate: new Date(selectedEstimate.estimatedShipmentDate),
                status: selectedEstimate.status,
                priority: selectedEstimate.priority,
                responsiblePerson: selectedEstimate.responsiblePerson,
                reasonForChange: selectedEstimate.reasonForChange
            });
        } else {
            const today = new Date();
            setFormData({
                orderNumber: '',
                customerName: '',
                productCode: '',
                productName: '',
                orderDate: today,
                currentEstimatedDate: today,
                estimatedShipmentDate: today,
                status: 'OnTime',
                priority: 'Normal',
                responsiblePerson: '',
                reasonForChange: ''
            });
        }
    }, [selectedEstimate]);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const handleNew = () => {
        setSelectedEstimate(null);
        setDisplayModal(true);
    };

    const handleEdit = (estimate) => {
        setSelectedEstimate(estimate);
        setDisplayModal(true);
    };

    const handleSave = () => {
        if (!formData.orderNumber || !formData.customerName || !formData.productCode) {
            return; // Basic validation
        }

        let reasonForChange = formData.reasonForChange;
        let status = formData.status;

        if (selectedEstimate) {
            const originalDate = formatDateForApi(selectedEstimate.originalEstimatedDate);
            const newCurrentDate = formatDateForApi(formData.currentEstimatedDate);

            if (originalDate !== newCurrentDate && !reasonForChange) {
                return; // Require reason for date change
            }

            if (originalDate < newCurrentDate && status === 'OnTime') {
                status = 'Delayed';
            } else if (originalDate > newCurrentDate && status === 'OnTime') {
                status = 'Expedited';
            }
        }

        if (selectedEstimate) {
            // Update existing estimate
            const updatedEstimates = estimates.map((estimate) =>
                estimate.id === selectedEstimate.id
                    ? {
                          ...estimate,
                          customerName: formData.customerName,
                          productCode: formData.productCode,
                          productName: formData.productName,
                          orderDate: formData.orderDate.toISOString(),
                          currentEstimatedDate: formData.currentEstimatedDate.toISOString(),
                          estimatedShipmentDate: formData.estimatedShipmentDate.toISOString(),
                          reasonForChange: reasonForChange,
                          status: status,
                          priority: formData.priority,
                          responsiblePerson: formData.responsiblePerson
                      }
                    : estimate
            );
            setEstimates(updatedEstimates);
        } else {
            // Add new estimate
            const newEstimate = {
                id: Math.floor(Math.random() * 10000),
                orderNumber: formData.orderNumber,
                customerName: formData.customerName,
                productCode: formData.productCode,
                productName: formData.productName,
                orderDate: formatDateForApi(formData.orderDate),
                originalEstimatedDate: formatDateForApi(formData.currentEstimatedDate),
                currentEstimatedDate: formatDateForApi(formData.currentEstimatedDate),
                estimatedShipmentDate: formatDateForApi(formData.estimatedShipmentDate),
                reasonForChange: reasonForChange || '',
                status: status || 'OnTime',
                priority: formData.priority || 'Normal',
                responsiblePerson: formData.responsiblePerson || ''
            };
            setEstimates([...estimates, newEstimate]);
        }

        setDisplayModal(false);
    };

    const handleRemove = () => {
        if (selectedEstimate) {
            const filteredEstimates = estimates.filter((estimate) => estimate.id !== selectedEstimate.id);
            setEstimates(filteredEstimates);
            setDisplayModal(false);
        }
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <i onClick={() => handleEdit(rowData)} className="pi pi-pencil" style={{ left: '1.5rem', color: '#0B3187' }}></i>
            </div>
        );
    };

    const priorityBodyTemplate = (rowData) => {
        let severity = '';

        switch (rowData.priority) {
            case 'Urgent':
                severity = 'danger';
                break;
            case 'High':
                severity = 'warning';
                break;
            case 'Normal':
                severity = 'info';
                break;
            case 'Low':
                severity = 'success';
                break;
            default:
                severity = 'info';
        }

        return <span className={`p-badge p-badge-${severity}`}>{getPriorityLabel(rowData.priority)}</span>;
    };

    const statusBodyTemplate = (rowData) => {
        let severity = '';

        switch (rowData.status) {
            case 'OnTime':
                severity = 'success';
                break;
            case 'Delayed':
                severity = 'danger';
                break;
            case 'AtRisk':
                severity = 'warning';
                break;
            case 'Expedited':
                severity = 'info';
                break;
            default:
                severity = 'info';
        }

        return <span className={`p-badge p-badge-${severity}`}>{getStatusLabel(rowData.status)}</span>;
    };

    const dateChangeTemplate = (rowData) => {
        const original = new Date(rowData.originalEstimatedDate);
        const current = new Date(rowData.currentEstimatedDate);

        if (original.getTime() === current.getTime()) {
            return <span>-</span>;
        }

        const diffDays = Math.round((current.getTime() - original.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays > 0) {
            return <span className="text-red-500">+{diffDays} gün</span>;
        } else {
            return <span className="text-green-500">{diffDays} gün</span>;
        }
    };

    const getPriorityLabel = (code) => {
        const option = priorityOptions.find((o) => o.code === code);
        return option ? option.name : code;
    };

    const getStatusLabel = (code) => {
        const option = statusOptions.find((o) => o.code === code);
        return option ? option.name : code;
    };

    const header = (
        <div className="flex justify-content-between align-items-center">
            <div className="flex align-items-center">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Ara..." />
                </span>
            </div>
            <div className="flex gap-2">
                <NewButton onClick={handleNew} />
            </div>
        </div>
    );

    const footerContent = (
        <div className="flex justify-content-end gap-2">
            {selectedEstimate && <Button label="Sil" icon="pi pi-trash" severity="danger" onClick={handleRemove} />}
            <Button label="İptal" icon="pi pi-times" outlined onClick={() => setDisplayModal(false)} />
            <Button label="Kaydet" icon="pi pi-check" onClick={handleSave} />
        </div>
    );

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">Tahmini Teslimat Tarihi Raporu</h1>
            </div>

            <Card>
                <DataTable
                    value={estimates}
                    paginator
                    className="p-datatable-gridlines"
                    showGridlines
                    rows={10}
                    dataKey="id"
                    loading={loading}
                    filters={filters}
                    filterDisplay="menu"
                    globalFilterFields={['orderNumber', 'customerName', 'productCode', 'productName', 'responsiblePerson']}
                    header={header}
                    emptyMessage="Tahmini teslimat tarihi kaydı bulunamadı."
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
                    tableStyle={{ minWidth: '50rem' }}
                >
                    <Column sortable bodyClassName="text-center" headerClassName="text-center" body={actionBodyTemplate} exportable={false} style={{ width: '5%', minWidth: '33px' }}></Column>
                    <Column field="orderNumber" header="Sipariş No" filter filterPlaceholder="Sipariş no ara..." sortable style={{ width: '150px' }} />
                    <Column field="customerName" header="Müşteri" filter filterPlaceholder="Müşteri ara..." sortable style={{ width: '200px' }} />
                    <Column field="productCode" header="Ürün Kodu" filter filterPlaceholder="Ürün kodu ara..." sortable style={{ width: '150px' }} />
                    <Column field="productName" header="Ürün Adı" filter filterPlaceholder="Ürün adı ara..." sortable style={{ width: '200px' }} />
                    <Column field="orderDate" header="Sipariş Tarihi" body={(rowData) => dateOnlyBodyTemplate(rowData, 'orderDate')} sortable style={{ width: '150px' }} />
                    <Column field="currentEstimatedDate" header="Tahmini Tarih" body={(rowData) => dateOnlyBodyTemplate(rowData, 'currentEstimatedDate')} sortable style={{ width: '150px' }} />
                    <Column field="dateChange" header="Tarih Değişimi" body={dateChangeTemplate} sortable style={{ width: '120px' }} />
                    <Column
                        field="status"
                        header="Durum"
                        body={statusBodyTemplate}
                        filter
                        filterElement={(options) => <Dropdown value={options.value} options={statusOptions} onChange={(e) => options.filterCallback(e.value)} optionLabel="name" optionValue="code" placeholder="Tümü" className="p-column-filter" />}
                        sortable
                        style={{ width: '120px' }}
                    />
                    <Column
                        field="priority"
                        header="Öncelik"
                        body={priorityBodyTemplate}
                        filter
                        filterElement={(options) => <Dropdown value={options.value} options={priorityOptions} onChange={(e) => options.filterCallback(e.value)} optionLabel="name" optionValue="code" placeholder="Tümü" className="p-column-filter" />}
                        sortable
                        style={{ width: '120px' }}
                    />
                    <Column field="responsiblePerson" header="Sorumlu Kişi" filter filterPlaceholder="Sorumlu ara..." sortable style={{ width: '150px' }} />
                </DataTable>
            </Card>

            {/* Edit/Create Modal */}
            <Dialog
                header={selectedEstimate ? 'Tahmini Teslimat Tarihini Düzenle' : 'Yeni Tahmini Teslimat Tarihi'}
                visible={displayModal}
                style={{ width: '80%', maxWidth: '900px' }}
                modal
                footer={footerContent}
                onHide={() => setDisplayModal(false)}
            >
                <div className="p-fluid grid">
                    <div className="field col-12 md:col-4">
                        <label htmlFor="orderNumber">Sipariş No*</label>
                        <InputText id="orderNumber" value={formData.orderNumber} onChange={(e) => setFormData({ ...formData, orderNumber: e.target.value })} required />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="customerName">Müşteri*</label>
                        <InputText id="customerName" value={formData.customerName} onChange={(e) => setFormData({ ...formData, customerName: e.target.value })} required />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="productCode">Ürün Kodu*</label>
                        <InputText id="productCode" value={formData.productCode} onChange={(e) => setFormData({ ...formData, productCode: e.target.value })} required />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="productName">Ürün Adı</label>
                        <InputText id="productName" value={formData.productName} onChange={(e) => setFormData({ ...formData, productName: e.target.value })} />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="orderDate">Sipariş Tarihi</label>
                        <Calendar id="orderDate" value={formData.orderDate} onChange={(e) => setFormData({ ...formData, orderDate: e.value })} dateFormat="dd/mm/yy" showIcon />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="currentEstimatedDate">Tahmini Teslimat Tarihi</label>
                        <Calendar id="currentEstimatedDate" value={formData.currentEstimatedDate} onChange={(e) => setFormData({ ...formData, currentEstimatedDate: e.value })} dateFormat="dd/mm/yy" showIcon />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="status">Durum</label>
                        <Dropdown id="status" value={formData.status} options={statusOptions} onChange={(e) => setFormData({ ...formData, status: e.value })} optionLabel="name" optionValue="code" placeholder="Durum Seçiniz" />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="priority">Öncelik</label>
                        <Dropdown id="priority" value={formData.priority} options={priorityOptions} onChange={(e) => setFormData({ ...formData, priority: e.value })} optionLabel="name" optionValue="code" placeholder="Öncelik Seçiniz" />
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="responsiblePerson">Sorumlu Kişi</label>
                        <InputText id="responsiblePerson" value={formData.responsiblePerson} onChange={(e) => setFormData({ ...formData, responsiblePerson: e.target.value })} />
                    </div>
                    <div className="field col-12">
                        <label htmlFor="reasonForChange">Tarih Değişim Nedeni</label>
                        <InputTextarea id="reasonForChange" value={formData.reasonForChange} onChange={(e) => setFormData({ ...formData, reasonForChange: e.target.value })} rows={3} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default EstimatedDeliveryDatePage;
